import { DEFAULT_WAVE_COLOR } from '@anm/constants/colors';
import { ENTER_NAME } from '@anm/constants/keyCodes';
import React, { useEffect, ChangeEvent, FC, KeyboardEvent, RefObject } from 'react';

import { ColorInput, InputWrapper } from './ColorPickerField/Wrapper';
import { ColorPickerBoard, ColorPickerBoardContainer, ColorPickerSlider, ColorPickerWrapper } from './Wrapper';
import useColorPicker from './hooks';

export type ColorPickerProps = {
  onColorChange: (color: string) => void;
  closeDropdown: () => void;
  withTextInput?: boolean;
  initialColor?: string;
  onFastColorChange?: (color: string) => void;
} & TextInputProps;

type TextInputProps = {
  value: string;
  inputRef: RefObject<HTMLInputElement>;
  onBlur: () => void;
  onFocus: () => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

const ColorPicker: FC<ColorPickerProps> = ({
  withTextInput,
  onColorChange,
  closeDropdown,
  initialColor = DEFAULT_WAVE_COLOR,
  onFastColorChange,
  ...inputProps
}) => {
  const { hue, value, setH, setSV, saturation, getBoardBg, currentColor } = useColorPicker(initialColor);
  const saveColor = () => onColorChange(currentColor);

  const handleKeyDown = ({ code }: KeyboardEvent<HTMLInputElement>) => {
    if (code !== ENTER_NAME) return;

    inputProps.inputRef.current?.blur();
    closeDropdown();
  };

  useEffect(() => {
    onFastColorChange?.(currentColor);
  }, [currentColor]);

  return (
    <ColorPickerWrapper>
      <ColorPickerBoardContainer>
        <ColorPickerBoard
          x={saturation}
          y={value}
          onChange={setSV}
          onComplete={saveColor}
          backgroundColor={getBoardBg()}
        />
        <ColorPickerSlider max={360} value={hue} vertical={true} onChange={setH} onComplete={saveColor} />
      </ColorPickerBoardContainer>
      {withTextInput && (
        <InputWrapper>
          <ColorInput type="text" {...inputProps} ref={inputProps.inputRef} onKeyDown={handleKeyDown} />
        </InputWrapper>
      )}
    </ColorPickerWrapper>
  );
};

export default ColorPicker;
